import { supabase } from "../supabase/supabaseClient";

/**
 * Get all public player profiles with optional filters
 */
export async function searchPublicPlayers({ positions = [], minAge = null, maxAge = null, minHeight = null, maxHeight = null, minWeight = null, maxWeight = null, city = null, team = null, q = null, footedness = null } = {}) {
    try {
        let query = supabase
            .from("player_profiles")
            .select("*")
            .eq("is_public", true)
            .order("created_at", { ascending: false });

        // Filter by positions (array contains)
        if (positions.length > 0) {
            query = query.overlaps("positions", positions);
        }

        // Filter by city
        if (city && city.trim()) {
            query = query.ilike("city", `%${city}%`);
        }

        // Filter by team
        if (team && team.trim()) {
            query = query.ilike("team", `%${team}%`);
        }

        // Free-text query for firstname or lastname
        if (q && q.trim()) {
            const term = q.trim();
            // PostgREST or() syntax: or(col.ilike.*term*,col.ilike.*term*)
            query = query.or(`firstname.ilike.%${term}%,lastname.ilike.%${term}%`);
        }

        // Filter by footedness (include 'Both' when Left or Right selected)
        if (footedness && footedness.trim()) {
            const f = footedness.trim();
            const values = f === "Left" ? ["Left", "Both"] : f === "Right" ? ["Right", "Both"] : ["Both"];
            query = query.in("footedness", values);
        }

        let { data, error } = await query;

        if (error) {
            console.error("Error searching players:", error);
            return { data: null, error };
        }

        // Filter by age on client side (since PostgreSQL date math can be complex)
        if (minAge !== null || maxAge !== null) {
            const today = new Date();
            data.forEach((player) => {
                if (player.DateOfBirth) {
                    const birth = new Date(player.DateOfBirth);
                    const age = Math.floor((today - birth) / (365.25 * 24 * 60 * 60 * 1000));
                    player._age = age;
                }
            });

            data = data.filter((player) => {
                const age = player._age;
                if (minAge !== null && age < minAge) return false;
                if (maxAge !== null && age > maxAge) return false;
                return true;
            });
        }

        // Filter by height
        if (minHeight !== null || maxHeight !== null) {
            data = data.filter((player) => {
                if (!player.height) return false;
                if (minHeight !== null && player.height < minHeight) return false;
                if (maxHeight !== null && player.height > maxHeight) return false;
                return true;
            });
        }

        // Filter by weight
        if (minWeight !== null || maxWeight !== null) {
            data = data.filter((player) => {
                if (!player.weight) return false;
                if (minWeight !== null && player.weight < minWeight) return false;
                if (maxWeight !== null && player.weight > maxWeight) return false;
                return true;
            });
        }

        return { data, error: null };
    } catch (err) {
        console.error("Search error:", err);
        return { data: null, error: err };
    }
}

/**
 * Get a single player profile by ID
 */
export async function getPlayerProfile(userId) {
    try {
        const { data, error } = await supabase
            .from("player_profiles")
            .select("*")
            .eq("id", userId)
            .single();

        if (error) {
            console.error("Error fetching player profile:", error);
            return { data: null, error };
        }

        return { data, error: null };
    } catch (err) {
        console.error("Fetch error:", err);
        return { data: null, error: err };
    }
}
